import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Circle, 
  CheckCircle2, 
  Loader2,
  ChevronDown,
  ChevronRight,
  Clock,
  X,
  Plus,
  Tag,
  FileText,
  Table2
} from 'lucide-react';

// Step 1: Input Form Component
export const PublishInputForm = ({ tableName: initialName, query, onCancel, onSubmit }) => {
  const [tableName, setTableName] = useState(initialName || 'Unified Customer 360 View');
  const [tags, setTags] = useState(['customer-data', 'unified-view']);
  const [newTag, setNewTag] = useState('');
  const [ignoreLimits, setIgnoreLimits] = useState(false);
  const [showQuery, setShowQuery] = useState(false);

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = () => {
    onSubmit({
      tableName,
      tags,
      removeLimits: ignoreLimits
    });
  };

  const defaultQuery = query || `SELECT source_system, customer_id, customer_name,
       city, country, region, industry
FROM unified_customer_360
WHERE active = true
LIMIT 1000;`;

  // Simple SQL syntax highlighting
  const renderHighlightedQuery = (sql) => {
    const keywords = ['SELECT', 'FROM', 'WHERE', 'LIMIT', 'AND', 'OR', 'ORDER', 'BY', 'GROUP', 'JOIN', 'ON', 'AS', 'INNER', 'LEFT', 'RIGHT', 'OUTER'];
    const booleans = ['true', 'false', 'null', 'NULL', 'TRUE', 'FALSE'];
    
    return sql.split('\n').map((line, lineIndex) => {
      const parts = line.split(/(\s+)/);
      return (
        <div key={lineIndex}>
          {parts.map((part, partIndex) => {
            const upperPart = part.toUpperCase();
            if (keywords.includes(upperPart)) {
              return <span key={partIndex} className="text-blue-400">{part}</span>;
            } else if (booleans.includes(part)) {
              return <span key={partIndex} className="text-emerald-400">{part}</span>;
            } else if (/^\d+$/.test(part)) {
              return <span key={partIndex} className="text-amber-400">{part}</span>;
            }
            return <span key={partIndex} className="text-gray-400">{part}</span>;
          })}
        </div>
      );
    });
  };

  return (
    <div className="rounded-xl bg-slate-950/80 overflow-hidden border border-slate-800/50">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-800/50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-3.5 h-3.5 text-blue-400" />
          <span className="text-xs font-medium text-white">Publish Data Table</span>
        </div>
        <button
          onClick={onCancel}
          className="p-1 text-gray-500 hover:text-white hover:bg-slate-800 rounded transition-colors"
          data-testid="publish-close-btn"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Form Content */}
      <div className="p-4 space-y-3">
        {/* Data Product Name */}
        <div>
          <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wide block mb-1.5">
            Data Product Name
          </label>
          <input
            type="text"
            value={tableName}
            onChange={(e) => setTableName(e.target.value)}
            className="w-full px-3 py-2 bg-slate-900/60 border border-slate-700/50 rounded-lg text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-blue-500/50 transition-colors"
            placeholder="Enter data product name..."
            data-testid="publish-data-product-name"
          />
        </div>

        {/* Tags */}
        <div>
          <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wide block mb-1.5">
            Tags
          </label>
          <div className="flex items-center gap-2 mb-2">
            <div className="flex-1 flex items-center px-3 py-1.5 bg-slate-900/60 border border-slate-700/50 rounded-lg">
              <Tag className="w-3 h-3 text-gray-500 mr-2" />
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 bg-transparent text-xs text-white placeholder:text-gray-500 focus:outline-none"
                placeholder="Add a tag..."
                data-testid="publish-tag-input"
              />
            </div>
            <button
              onClick={handleAddTag}
              className="p-1.5 bg-slate-800 hover:bg-slate-700 text-gray-400 hover:text-white rounded-lg transition-colors"
              data-testid="publish-add-tag-btn"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          {/* Tag Pills */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/15 text-blue-400 text-xs rounded"
                >
                  {tag}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    className="hover:text-white transition-colors"
                    data-testid={`publish-remove-tag-${tag}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-slate-700/50"></div>

        {/* Ignore Limits Toggle */}
        <div className="flex items-center justify-between py-1">
          <div>
            <div className="text-xs text-gray-300">Ignore Limits</div>
            <div className="text-[10px] text-gray-500">Bypass query row limits during deployment</div>
          </div>
          {/* Toggle Switch */}
          <button
            onClick={() => setIgnoreLimits(!ignoreLimits)}
            className={`relative w-10 h-5 rounded-full transition-colors ${
              ignoreLimits ? 'bg-blue-600' : 'bg-slate-700'
            }`}
            data-testid="publish-ignore-limits-toggle"
          >
            <span
              className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                ignoreLimits ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        {/* Query Preview - Collapsible */}
        <div className="bg-slate-900/60 rounded-lg border border-slate-700/30 overflow-hidden">
          <button
            onClick={() => setShowQuery(!showQuery)}
            className="w-full px-3 py-2 flex items-center justify-between hover:bg-slate-800/30 transition-colors"
            data-testid="publish-query-toggle"
          >
            <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Query Preview</span>
            {showQuery ? (
              <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 text-gray-500" />
            )}
          </button>
          {showQuery && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="px-3 pb-3"
            >
              <pre className="text-[10px] font-mono leading-relaxed overflow-x-auto" data-testid="publish-query-preview">
                {renderHighlightedQuery(defaultQuery)}
              </pre>
            </motion.div>
          )}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="px-4 py-3 border-t border-slate-800/50 flex items-center justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-xs text-gray-400 hover:text-white transition-colors"
          data-testid="publish-cancel-btn"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={!tableName.trim()}
          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-gray-500 text-white text-xs font-medium rounded-lg transition-colors"
          data-testid="publish-submit-btn"
        >
          Publish
        </button>
      </div>
    </div>
  );
};

// Step 2: Progress Component (Same styling as QueryPlan)
export const PublishProgress = ({ tableName, tags = [], removeLimits = false, onComplete, onPreviewClick }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [visibleSteps, setVisibleSteps] = useState(0);
  const [stepStatuses, setStepStatuses] = useState(['pending', 'pending', 'pending', 'pending']);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  const animationStarted = useRef(false);
  const completionTriggered = useRef(false);
  const onCompleteRef = useRef(onComplete);
  const startTimeRef = useRef(null);

  // Mock metadata - in real app this would come from the publish response
  const metadata = {
    rows: '12,847',
    columns: 7
  };

  const steps = [
    { id: 1, text: 'Refreshing data' },
    { id: 2, text: 'Data Profile Generation' },
    { id: 3, text: 'Metadata generation' },
    { id: 4, text: 'Semantic layer persistence' }
  ];

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Initialize start time on mount
  useEffect(() => {
    startTimeRef.current = Date.now();
  }, []);

  // Timer for elapsed time - stops when all complete
  useEffect(() => {
    const allComplete = stepStatuses.every(s => s === 'completed');
    if (allComplete) return; // Don't run timer if already complete
    
    const timer = setInterval(() => {
      if (startTimeRef.current) {
        setElapsedTime(((Date.now() - startTimeRef.current) / 1000).toFixed(1));
      }
    }, 100);
    return () => clearInterval(timer);
  }, [stepStatuses]);

  useEffect(() => {
    if (animationStarted.current) return;
    animationStarted.current = true;

    const stepsCount = 4;
    
    // Animate steps one by one
    for (let index = 0; index < stepsCount; index++) {
      // Show step as in_progress
      setTimeout(() => {
        setVisibleSteps(index + 1);
        setStepStatuses(prev => {
          const updated = [...prev];
          updated[index] = 'in_progress';
          return updated;
        });
      }, 800 + (index * 1500));
      
      // Mark as complete
      setTimeout(() => {
        setStepStatuses(prev => {
          const updated = [...prev];
          updated[index] = 'completed';
          return updated;
        });
        
        // Trigger completion and auto-collapse after last step
        if (index === stepsCount - 1 && !completionTriggered.current) {
          completionTriggered.current = true;
          setTimeout(() => {
            setIsExpanded(false);
            if (onCompleteRef.current) onCompleteRef.current();
          }, 600);
        }
      }, 800 + (index * 1500) + 1000);
    }
  }, []);

  const allComplete = stepStatuses.every(s => s === 'completed');
  const completedCount = stepStatuses.filter(s => s === 'completed').length;
  const progressPercent = (completedCount / steps.length) * 100;

  return (
    <div className={`relative rounded-xl overflow-hidden ${allComplete ? 'graph-ready-shimmer border border-blue-500/25' : 'border border-slate-800/50 bg-slate-950/50'}`}>
      {/* Shimmer overlay - only when complete */}
      {allComplete && <div className="shimmer-effect absolute inset-0 pointer-events-none" />}
      
      {/* Header */}
      <div
        onClick={() => {
          if (allComplete && onPreviewClick) {
            onPreviewClick();
          }
        }}
        className={`relative w-full text-left px-5 pt-3 pb-4 flex items-center justify-between transition-colors ${allComplete ? 'hover:bg-blue-500/5 cursor-pointer' : ''}`}
        data-testid="publish-progress-header"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className={`flex-shrink-0 rounded-full flex items-center justify-center ${allComplete ? 'w-8 h-8 bg-blue-500/10' : 'w-6 h-6 bg-blue-500/10'}`}>
            <Table2 className={`text-blue-400 ${allComplete ? 'w-5 h-5' : 'w-3.5 h-3.5'}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className={`text-sm font-medium truncate ${allComplete ? 'text-blue-400' : 'text-gray-300'}`}>
                {allComplete ? tableName : `Publishing: ${tableName}`}
              </h3>
              {/* Chevron - separate click for expand/collapse */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
                className={`shrink-0 p-1 rounded hover:bg-slate-700/50 transition-colors ${allComplete ? 'text-blue-400' : 'text-gray-500'}`}
                data-testid="publish-expand-btn"
              >
                {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>
            </div>
            {allComplete && (
              <p className="text-xs text-gray-400 mt-0.5">
                {metadata.rows} rows · {metadata.columns} columns
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {allComplete ? (
            <span className="flex items-center gap-1.5 text-xs text-blue-400 bg-blue-500/10 px-2.5 py-1 rounded-full">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Published
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-[10px] text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full">
              <Loader2 className="w-3 h-3 animate-spin" />
              Publishing
            </span>
          )}
        </div>
      </div>

      {/* Content - Show during progress OR when expanded after complete */}
      {isExpanded && (
        <div className="relative px-5 pb-4 -mt-1">
          {/* Progress Bar - Only during progress */}
          {!allComplete && (
            <div className="mb-3 pb-3 border-b border-slate-800/50">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] text-gray-500">{completedCount} of {steps.length} steps complete</span>
                <span className="text-[10px] text-gray-500 font-mono">{elapsedTime}s</span>
              </div>
              <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-blue-500 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercent}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>
          )}

          {/* Tags - Show when complete (1st) */}
          {allComplete && tags.length > 0 && (
            <div className="mb-3">
              <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Tags</span>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-2 py-0.5 bg-blue-500/10 text-blue-400 text-[10px] rounded"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Query Limits - Show when complete (2nd) */}
          {allComplete && (
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-800/30">
              <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Query Limits</span>
              <span className={`text-[10px] px-2 py-0.5 rounded ${removeLimits ? 'bg-amber-500/10 text-amber-400' : 'bg-slate-700/50 text-gray-400'}`}>
                {removeLimits ? 'Ignored' : 'Applied'}
              </span>
            </div>
          )}

          {/* Steps (3rd - at bottom when complete, or during progress) */}
          <div>
            <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
              {allComplete ? 'Completed Steps' : 'Publishing Steps'}
            </span>
            <div className="space-y-1.5 mt-1.5">
              {(allComplete ? steps : steps.slice(0, visibleSteps)).map((step, index) => {
                const status = allComplete ? 'completed' : stepStatuses[index];
                return (
                  <motion.div
                    key={step.id}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2"
                  >
                    {status === 'completed' ? (
                      <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                    ) : status === 'in_progress' ? (
                      <Loader2 className="w-3 h-3 text-blue-400 flex-shrink-0 animate-spin" />
                    ) : (
                      <Circle className="w-3 h-3 text-gray-600 flex-shrink-0" />
                    )}
                    <span className={`text-[10px] ${status === 'completed' ? 'text-gray-400' : 'text-gray-500'}`}>
                      {step.text}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default { PublishInputForm, PublishProgress };
